// src/components/admin/CrudPage.jsx
// Generic list + modal CRUD component used by all admin pages
import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../common/Modal';
import ConfirmDialog from '../common/ConfirmDialog';
import { useToast } from '../common/Toast';
import { Edit2, Trash2, Plus, Inbox, Loader2 } from 'lucide-react';

/**
 * Props:
 *  - title, subtitle
 *  - columns: [{ key, label, render? }]
 *  - fetchFn: () => Promise<{ data: { data: rows } }>
 *  - createFn, updateFn(id, data), deleteFn(id)
 *  - FormComponent: ({ form, onChange, categories, ... }) => JSX
 *  - formDefault: object
 *  - extraProps: extra data to pass to FormComponent
 *  - searchKey: field name to search on (default: 'name')
 *  - renderFilters: (rows) => JSX
 *  - customFilter: (row) => boolean
 */
const CrudPage = ({
  title, subtitle,
  columns, fetchFn, createFn, updateFn, deleteFn,
  FormComponent, formDefault = {},
  extraProps = {},
  searchKey = 'name',
  renderFilters,
  customFilter,
}) => {
  const [rows,     setRows]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [modal,    setModal]    = useState({ open: false, mode: 'create', row: null });
  const [confirm,  setConfirm]  = useState({ open: false, id: null });
  const [form,     setForm]     = useState(formDefault);
  const [saving,   setSaving]   = useState(false);
  const { show, ToastComponent } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchFn();
      setRows(res.data.data || []);
    } catch { show('Failed to load data', 'error'); }
    finally { setLoading(false); }
  }, [fetchFn]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(formDefault); setModal({ open: true, mode: 'create', row: null }); };
  const openEdit   = (row) => { setForm({ ...row }); setModal({ open: true, mode: 'edit', row }); };
  const closeModal = () => setModal(p => ({ ...p, open: false }));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal.mode === 'create') {
        await createFn(form);
        show(`${title.slice(0, -1)} created`, 'success');
      } else {
        await updateFn(modal.row.id, form);
        show(`${title.slice(0, -1)} updated`, 'success');
      }
      closeModal();
      load();
    } catch (err) {
      show(err.response?.data?.message || 'Save failed', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteFn(id);
      show('Deleted successfully', 'success');
      load();
    } catch { show('Delete failed', 'error'); }
  };

  const filtered = rows.filter(r => {
    const searchMatch = search === '' || String(r[searchKey] || '').toLowerCase().includes(search.toLowerCase());
    const customMatch = customFilter ? customFilter(r) : true;
    return searchMatch && customMatch;
  });

  return (
    <div>
      {ToastComponent}
      <div className="page-header">
        <div>
          <h2 className="page-title">{title}</h2>
          {subtitle && <p className="page-subtitle">{subtitle}</p>}
        </div>
        {createFn && (
          <button className="btn btn-primary" onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={16} /> Add {title.replace(/s$/, '')}
          </button>
        )}
      </div>

      <div className="card">
        <div className="card-header" style={{ flexWrap: 'wrap', gap: 12 }}>
          <input
            className="form-input"
            placeholder={`Search ${title.toLowerCase()}…`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ maxWidth: 280, margin: 0 }}
          />
          {renderFilters && renderFilters()}
          <span className="text-muted">{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {loading ? (
          <div className="flex-center" style={{ padding: 64 }}><Loader2 className="spinner" size={32} /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><Inbox size={40} /></div>
            <p>{search ? 'No results found' : `No ${title.toLowerCase()} yet`}</p>
            {createFn && !search && (
              <button className="btn btn-primary btn-sm" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }} onClick={openCreate}>
                <Plus size={16} /> Add first {title.replace(/s$/, '').toLowerCase()}
              </button>
            )}
          </div>
        ) : (
          <div className="table-container" style={{ border: 'none', borderRadius: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  {columns.map(c => <th key={c.key}>{c.label}</th>)}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => (
                  <tr key={row.id}>
                    {columns.map(c => (
                      <td key={c.key}>{c.render ? c.render(row) : row[c.key] ?? '—'}</td>
                    ))}
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {updateFn && (
                          <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEdit(row)} title="Edit"><Edit2 size={16} /></button>
                        )}
                        {deleteFn && (
                          <button className="btn btn-ghost btn-sm btn-icon" style={{ color: 'var(--danger)' }}
                            onClick={() => setConfirm({ open: true, id: row.id })} title="Delete"><Trash2 size={16} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      <Modal
        isOpen={modal.open}
        onClose={closeModal}
        title={modal.mode === 'create' ? `Add ${title.replace(/s$/, '')}` : `Edit ${title.replace(/s$/, '')}`}
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ display: 'flex', alignItems: 'center' }}>
              {saving ? <><Loader2 className="spinner" size={14} style={{ marginRight: 6 }} /> Saving…</> : 'Save'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <FormComponent form={form} onChange={handleChange} setForm={setForm} {...extraProps} />
        </div>
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog
        isOpen={confirm.open}
        onClose={() => setConfirm({ open: false, id: null })}
        onConfirm={() => handleDelete(confirm.id)}
        title="Confirm Delete"
        message="Are you sure you want to delete this record? This action cannot be undone."
        danger
      />
    </div>
  );
};

export default CrudPage;
